import { useState, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiClient, API, formatApiErrorDetail } from "@/lib/api";
import { AdminGuard } from "@/components/admin/AdminShell";

const SECTIONS = [
  ["company", "Entreprise", [
    ["name", "Nom commercial"], ["operator_name", "Exploitant"], ["legal_form", "Forme juridique"],
    ["short_description", "Description courte"], ["long_description", "Description longue"],
    ["siren", "SIREN"], ["siret", "SIRET"], ["ape_code", "Code APE (facultatif)"], ["vat_number", "TVA intracommunautaire"],
    ["address", "Adresse"], ["address_line_2", "Complément d'adresse"], ["postal_code", "Code postal"],
    ["city", "Ville"], ["country", "Pays"],
    ["phone", "Téléphone principal"], ["phone_secondary", "Téléphone secondaire"],
    ["email", "E-mail général"], ["email_sales", "E-mail commercial"], ["email_accounting", "E-mail comptabilité"],
    ["website", "Site internet"], ["logo_url", "Logo (URL)"],
    ["signature", "Signature commerciale"], ["footer_text", "Pied de page documents"],
    ["legal_mentions", "Mentions légales complémentaires"], ["mediator", "Médiateur de la consommation"],
    ["hosting_provider", "Hébergeur du site"],
    ["language", "Langue"], ["currency", "Devise"], ["timezone", "Fuseau horaire"], ["date_format", "Format de date"],
  ]],
  ["branding", "Identité visuelle", [
    ["primary_color", "Couleur principale"], ["accent_color", "Couleur d'accent"],
    ["logo_dark_url", "Logo sombre (URL)"], ["logo_light_url", "Logo clair (URL)"],
    ["favicon_url", "Favicon (URL)"], ["cover_image_url", "Visuel de couverture devis"]]],
  ["quote", "Devis", [["prefix", "Préfixe numérotation"], ["validity_days", "Validité (jours)"], ["vat_rate", "TVA (%)"],
    ["default_conditions", "Conditions par défaut"], ["default_intro", "Introduction par défaut"], ["default_next_steps", "Prochaines étapes par défaut"]]],
  ["invoicing", "Facturation", [["prefix", "Préfixe"], ["due_days", "Échéance (jours)"],
    ["vat_enabled", "TVA activée sur les documents"], ["vat_rate", "TVA (%)"], ["vat_regime", "Régime de TVA"],
    ["fiscal_text", "Mention fiscale (affichée si TVA à 0)"],
    ["iban", "IBAN"], ["bic", "BIC"], ["bank_name", "Banque"], ["payment_conditions", "Conditions de règlement"], ["mentions", "Mentions"]]],
  ["payments", "Paiements manuels", [["instructions", "Instructions de règlement"],
    ["bank_beneficiary", "Bénéficiaire"], ["bank_iban_display", "IBAN affiché"], ["bank_iban", "IBAN normalisé (traitements internes)"],
    ["bank_bic", "BIC / SWIFT"], ["bank_name", "Banque"], ["bank_address", "Adresse de la banque"],
    ["bank_currency", "Devise"], ["bank_correspondent_bic", "BIC correspondant (facultatif, jamais affiché)"],
    ["bank_transfer_instructions", "Instructions de virement"],
    ["bank_account_active", "Compte actif"], ["bank_display_enabled", "Affichage des coordonnées activé"],
    ["rib", "RIB texte libre (ancien champ, facultatif)"],
    ["deposit_default_mode", "Mode d'acompte (none/fixed/percent)"], ["deposit_default_value", "Valeur d'acompte"],
    ["scheduling_policy", "Politique de planification"]]],
  ["communications", "Communications", [
    ["sender_name", "Nom d'expéditeur"], ["reply_to", "Adresse de réponse"], ["signature_html", "Signature"],
    ["sender_general_name", "Profil Général — nom"], ["sender_general_email", "Profil Général — e-mail"],
    ["sender_commercial_name", "Profil Commercial — nom"], ["sender_commercial_email", "Profil Commercial — e-mail"],
    ["sender_exploitation_name", "Profil Exploitation — nom"], ["sender_exploitation_email", "Profil Exploitation — e-mail"],
    ["sender_comptabilite_name", "Profil Comptabilité — nom"], ["sender_comptabilite_email", "Profil Comptabilité — e-mail"],
  ]],
  ["followups", "Relances devis", [
    ["enabled", "Relances automatiques activées"],
    ["level1_days", "1ʳᵉ relance (jours après envoi)"],
    ["level2_days", "2ᵉ relance (jours après envoi)"],
    ["level3_days", "Dernière relance (jours après envoi)"],
  ]],
  ["security", "Sécurité", [["magic_link_days", "Durée des liens magiques (jours)"]]],
];

const SECTION_NOTES = {
  company: "Informations provisoires centralisées : remplacez-les ici à réception du nouveau Kbis, sans modification du code. SIREN, adresse et ville sont obligatoires pour émettre des factures. Aucune information légale n'est jamais inventée : les champs vides restent masqués sur les documents.",
  invoicing: "La TVA n'est jamais déduite automatiquement. Si une TVA est appliquée sans numéro intracommunautaire renseigné, l'émission définitive de facture est bloquée (les brouillons restent possibles).",
  payments: "Compte bancaire provisoire remplaçable ici sans changement de code. Le RIB n'apparaît jamais sur le site public : il est affiché uniquement au client concerné, lorsque le règlement par virement est configuré pour son dossier (portail sécurisé, facture émise, e-mail de demande de règlement). Le BIC correspondant n'est jamais affiché dans les documents standards.",
  communications: "Les adresses des profils expéditeurs ne sont jamais inventées : tant qu'un profil n'a pas d'e-mail, le profil par défaut (SENDER_EMAIL) est utilisé.",
  followups: "L'activation nécessite : Resend configuré (RESEND_API_KEY + SENDER_EMAIL), PORTAL_BASE_URL défini et modèles de relance prévisualisés (Communications > Modèles). Les relances s'arrêtent automatiquement dès que le client répond, accepte, refuse ou qu'une nouvelle version est envoyée.",
};

export default function SettingsPage() {
  const [active, setActive] = useState("company");
  const [data, setData] = useState(null);
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (active === "pricing") {
      apiClient.get("/admin/pricing-rules").then((r) => setRules(r.data.items)).catch(() => {});
    } else {
      apiClient.get(`/admin/settings/${active}`).then((r) => setData(r.data.data)).catch(() => {});
    }
  }, [active]);
  useEffect(() => { setData(null); setRules(null); load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/admin/settings/${active}`, { data });
      toast.success("Paramètres enregistrés");
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(det?.blockers ? `${det.message} : ${det.blockers.join(" • ")}` : formatApiErrorDetail(det));
    }
    finally { setSaving(false); }
  };

  const updateRule = async (rule, field, value) => {
    try {
      await apiClient.patch(`/admin/pricing-rules/${rule.id}`, { [field]: value });
      setRules((rs) => rs.map((r) => (r.id === rule.id ? { ...r, [field]: value } : r)));
      toast.success("Règle mise à jour");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const section = SECTIONS.find(([k]) => k === active);
  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Paramètres</h1>
      <div className="flex flex-wrap gap-2 mb-4">
        {SECTIONS.map(([k, l]) => (
          <button key={k} data-testid={`settings-tab-${k}`} onClick={() => setActive(k)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${active === k ? "bg-navy text-white" : "bg-white text-navy"}`}>{l}</button>
        ))}
        <button data-testid="settings-tab-pricing" onClick={() => setActive("pricing")}
          className={`px-3 py-1.5 rounded-full text-sm font-semibold ${active === "pricing" ? "bg-navy text-white" : "bg-white text-navy"}`}>Tarification</button>
        <a href={`${API}/admin/export/contacts.csv`} className="ml-auto"><Button variant="outline" className="rounded-xl" data-testid="settings-export-contacts">Export contacts CSV</Button></a>
      </div>

      {active === "pricing" ? (
        !rules ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-navy mt-10" /> : (
          <div className="bg-white rounded-2xl shadow-sm divide-y" data-testid="pricing-rules-list">
            {rules.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`pricing-rule-${r.code}`}>
                <div className="mr-auto"><div className="text-sm font-semibold text-navy">{r.label}</div>
                  <div className="text-[11px] text-muted-foreground">{r.code} • {r.category} • {r.mode}</div></div>
                {r.mode === "percent" ? (
                  <div className="flex items-center gap-1">
                    <Input type="number" defaultValue={r.percent} className="h-8 w-20 bg-white text-right"
                      data-testid={`rule-percent-${r.code}`}
                      onBlur={(e) => parseFloat(e.target.value) !== r.percent && updateRule(r, "percent", parseFloat(e.target.value) || 0)} />
                    <span className="text-xs">%</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <Input type="number" defaultValue={(r.amount_cents || 0) / 100} className="h-8 w-24 bg-white text-right"
                      data-testid={`rule-amount-${r.code}`}
                      onBlur={(e) => Math.round(parseFloat(e.target.value || 0) * 100) !== r.amount_cents && updateRule(r, "amount_cents", Math.round(parseFloat(e.target.value || 0) * 100))} />
                    <span className="text-xs">€{r.unit ? `/${r.unit}` : ""}</span>
                  </div>
                )}
                <label className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={r.active} data-testid={`rule-active-${r.code}`}
                    onChange={(e) => updateRule(r, "active", e.target.checked)} /> Active
                </label>
              </div>
            ))}
          </div>
        )
      ) : !data ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-navy mt-10" /> : (
        <div className="bg-white rounded-2xl shadow-sm p-5 max-w-2xl" data-testid={`settings-form-${active}`}>
          {section[2].map(([field, label]) => (
            <div key={field} className="mb-3">
              {typeof data[field] === "boolean" ? (
                <label className="flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer">
                  <input type="checkbox" data-testid={`settings-${active}-${field}`} checked={!!data[field]}
                    onChange={(e) => setData((p) => ({ ...p, [field]: e.target.checked }))} />
                  {label}
                </label>
              ) : (
                <>
                  <label className="text-xs font-semibold text-navy">{label}</label>
                  {String(data[field] ?? "").length > 80 || field.includes("conditions") || field.includes("intro") || field.includes("steps") || field.includes("signature") || field.includes("mentions") || field.includes("instructions") || field.includes("description") ? (
                    <Textarea data-testid={`settings-${active}-${field}`} value={data[field] ?? ""} rows={3}
                      onChange={(e) => setData((p) => ({ ...p, [field]: e.target.value }))} className="bg-white" />
                  ) : (
                    <Input data-testid={`settings-${active}-${field}`} value={data[field] ?? ""}
                      onChange={(e) => setData((p) => ({ ...p, [field]: e.target.value }))} className="bg-white" />
                  )}
                </>
              )}
            </div>
          ))}
          <Button data-testid="settings-save" onClick={save} disabled={saving} className="bg-navy text-white rounded-lg">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
          {SECTION_NOTES[active] && (
            <p className="text-xs text-muted-foreground mt-3" data-testid={`settings-note-${active}`}>{SECTION_NOTES[active]}</p>
          )}
        </div>
      )}
    </AdminGuard>
  );
}
